<template>
  <article
    class="lead-flow-card ui-kanban-card"
    :class="[
      { 'lead-flow-card--multi-activity': hasMultipleActivities },
      activityTheme ? `lead-flow-card--activity-${activityTheme}` : ''
    ]"
    :style="activityCardStyle"
    role="button"
    tabindex="0"
    :aria-label="`Open ${leadName}`"
    @click="openLead"
    @keydown.enter.prevent="openLead"
  >
    <div class="lead-flow-card__identity ui-kanban-card__identity">
      <span
        v-tooltip="lead.country && lead.country.name ? { content: countryName, placement: 'top', offset: 8, classes: 'tooltip--new' } : null"
        class="lead-flow-card__country ui-kanban-card__country ui-tooltip-target"
        :tabindex="lead.country && lead.country.name ? 0 : null"
        :aria-label="countryName"
      >
        <span :class="`fflag ff-sm fflag-${countryCode}`" aria-hidden="true"></span>
      </span>
      <AppTableText :text="lead.name" fallback="Unnamed lead" class="lead-flow-card__name ui-kanban-card__name" />
      <span
        v-if="managerName"
        v-tooltip="{ content: managerName, placement: 'top', offset: 8, classes: 'tooltip--new' }"
        class="lead-flow-card__manager ui-kanban-card__manager ui-tooltip-target"
        tabindex="0"
        :aria-label="managerName"
      >
        <img
          v-if="managerPhoto && !managerPhotoFailed"
          :src="managerPhoto"
          alt=""
          @error="managerPhotoFailed = true"
        >
        <span v-else>{{ managerInitials }}</span>
      </span>
    </div>

    <div class="lead-flow-card__tags ui-kanban-card__tags">
      <LeadManagementGuidanceIcon :status="lead.management_guidance_status" :comment="lead.management_guidance_comment" />
      <AppStatusBadge
        v-if="showStage && stageName"
        design="new"
        class="lead-flow-card__stage ui-kanban-card__stage"
        :color="lead.stage && lead.stage.color"
        :label="stageName"
      />
      <LeadActivityIcons
        design="new"
        icon-set="crm"
        icon-only
        keyboard-tooltips
        class="lead-flow-card__activities ui-kanban-card__activities"
        :activities="lead.activities"
        :max-visible="4"
      />
      <span
        v-tooltip="Number.isFinite(partyCount) && partyTooltip !== 'Party not provided' ? { content: partyTooltip, placement: 'top', offset: 8, classes: 'tooltip--new' } : null"
        class="lead-flow-card__party ui-kanban-card__party ui-tooltip-target"
        :tabindex="Number.isFinite(partyCount) && partyTooltip !== 'Party not provided' ? 0 : null"
      >
        <img
          class="lead-flow-card__party-icon ui-kanban-card__party-icon"
          src="@/assets/img/personal-details.svg"
          alt=""
          aria-hidden="true"
        >
        <span>{{ partyCount }}</span>
      </span>
    </div>

    <LeadFollowUpStrip
      v-if="lead.follow_up"
      :follow-up="lead.follow_up"
      :now="now"
    />

  </article>
</template>

<script>
import AppTableText from '@/components/AppTableText.vue'
import LeadActivityIcons from '@/views/crm/task-tracker/LeadActivityIcons.vue'
import LeadFollowUpStrip from '@/views/crm/task-tracker/LeadFollowUpStrip.vue'
import LeadManagementGuidanceIcon from '@/views/crm/task-tracker/LeadManagementGuidanceIcon.vue'

const ACTIVITY_ALIASES = {
  day_trips: 'day_trip',
  island_vacation: 'island'
}

const ACTIVITY_CARD_OPTIONS = {
  safari: {
    background: '#fff4df',
    border: '#eb9514'
  },
  climbing: {
    background: '#edf6e5',
    border: '#219b52'
  },
  day_trip: {
    background: '#e8f6fc',
    border: '#3d9dd3'
  },
  island: {
    background: '#e6f7f7',
    border: '#28a8aa'
  }
}

export default {
  name: 'LeadFlowCard',
  components: {
    AppTableText,
    LeadActivityIcons,
    LeadFollowUpStrip,
    LeadManagementGuidanceIcon
  },
  props: {
    lead: {
      type: Object,
      required: true
    },
    showStage: {
      type: Boolean,
      default: false
    },
    now: {
      type: Number,
      default: () => Date.now()
    }
  },
  data() {
    return {
      managerPhotoFailed: false
    }
  },
  watch: {
    lead() {
      this.managerPhotoFailed = false
    }
  },
  computed: {
    countryCode() {
      return this.lead.country && this.lead.country.code ? this.lead.country.code : 'empty'
    },
    countryName() {
      return this.lead.country && this.lead.country.name ? this.lead.country.name : 'Country not provided'
    },
    leadName() {
      return this.lead.name || 'Unnamed lead'
    },
    stageName() {
      return this.lead.stage && this.lead.stage.name ? this.lead.stage.name : ''
    },
    partyCount() {
      const party = this.lead.party || {}
      return Number.isFinite(party.total) ? party.total : '—'
    },
    partyTooltip() {
      const party = this.lead.party || {}
      const parts = [
        this.partyPart('Adults', party.adults),
        this.partyPart('Teenagers', party.teenagers),
        this.partyPart('Children', party.children)
      ].filter(Boolean)
      return parts.length ? parts.join(' · ') : 'Party not provided'
    },
    activityThemes() {
      const activities = Array.isArray(this.lead.activities) ? this.lead.activities : []
      return [
        ...new Set(activities.map(activity => {
          const code = typeof activity === 'string' ? activity : activity && activity.code
          return ACTIVITY_ALIASES[code] || code
        }).filter(code => ACTIVITY_CARD_OPTIONS[code]))
      ]
    },
    activityTheme() {
      return this.activityThemes.length === 1 ? this.activityThemes[0] : ''
    },
    hasMultipleActivities() {
      return this.activityThemes.length > 1
    },
    activityCardStyle() {
      if (!this.hasMultipleActivities) return {}

      const lastColorStop = 78
      const stops = this.activityThemes.map((code, index) => {
        const position = Math.round((index * lastColorStop) / (this.activityThemes.length - 1))
        return `${ACTIVITY_CARD_OPTIONS[code].background} ${position}%`
      })
      const firstActivity = ACTIVITY_CARD_OPTIONS[this.activityThemes[0]]
      return {
        background: `linear-gradient(135deg, ${stops.join(', ')}, #fff 100%)`,
        borderLeftColor: firstActivity.border
      }
    },
    managerName() {
      return this.lead.manager && this.lead.manager.name ? this.lead.manager.name : ''
    },
    managerPhoto() {
      return this.lead.manager && this.lead.manager.photo ? this.lead.manager.photo : ''
    },
    managerInitials() {
      return this.managerName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    }
  },
  methods: {
    openLead(event) {
      this.$emit('open', {
        leadId: this.lead.id,
        triggerElement: event.currentTarget
      })
    },
    partyPart(label, value) {
      return Number.isFinite(value) ? `${label}: ${value}` : null
    }
  }
}
</script>

<style scoped>

.lead-flow-card::after {
  position: absolute;
  z-index: 0;
  top: 46px;
  right: -18px;
  width: 92px;
  height: 92px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 70px;
  content: '';
  opacity: .045;
}

.lead-flow-card > * {
  position: relative;
  z-index: 1;
}

.lead-flow-card--activity-safari {
  border-left-color: #eb9514;
  background: linear-gradient(135deg, #fff7e9 0%, #fffcf6 56%, #fff 100%);
}

.lead-flow-card--activity-safari::after {
  background-image: url('~@/assets/img/lead-flow/activity-safari.png');
}

.lead-flow-card--activity-climbing {
  border-left-color: #219b52;
  background: linear-gradient(135deg, #f4f8ec 0%, #fbfdf8 56%, #fff 100%);
}

.lead-flow-card--activity-climbing::after {
  background-image: url('~@/assets/img/lead-flow/activity-climbing.png');
}

.lead-flow-card--activity-day_trip {
  border-left-color: #3d9dd3;
  background: linear-gradient(135deg, #edf8fd 0%, #f8fcfe 56%, #fff 100%);
}

.lead-flow-card--activity-day_trip::after {
  background-image: url('~@/assets/img/lead-flow/activity-day-trip.png');
}

.lead-flow-card--activity-island {
  border-left-color: #28a8aa;
  background: linear-gradient(135deg, #ecf9f9 0%, #f8fdfd 56%, #fff 100%);
}

.lead-flow-card--activity-island::after {
  background-image: url('~@/assets/img/lead-flow/activity-island.png');
}

.lead-flow-card--multi-activity::after {
  display: none;
}

</style>
